import { useRef } from 'react'
import { Clock, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/utils/cn'

interface TimePickerProps {
  /** HH:mm (24h) — empty string when unset. */
  value?: string
  onChange: (value: string) => void
  /** Shown when no time is selected (native time inputs have no placeholder). */
  placeholder?: string
  /** Native min/max (HH:mm) */
  min?: string
  max?: string
  step?: number | string
  disabled?: boolean
  className?: string
  id?: string
  'aria-label'?: string
}

/**
 * Time picker matching `DatePicker` grammar: visible empty-state label, always-
 * visible clock icon, click-anywhere opens the native picker via `showPicker()`,
 * and an inline × to clear without opening the clock.
 */
export function TimePicker({
  value = '',
  onChange,
  placeholder = 'Select time',
  min,
  max,
  step,
  disabled,
  className,
  id,
  'aria-label': ariaLabel,
}: TimePickerProps) {
  const empty = !value
  const inputRef = useRef<HTMLInputElement>(null)

  const openPicker = () => {
    const el = inputRef.current
    if (!el || el.disabled) return
    const withPicker = el as HTMLInputElement & { showPicker?: () => void }
    if (typeof withPicker.showPicker === 'function') {
      try {
        withPicker.showPicker()
        return
      } catch {
        // Not supported in this context (e.g. no user-gesture) — fall back below.
      }
    }
    el.focus()
  }

  return (
    <div className={cn('relative w-full min-w-0', disabled && 'opacity-60')}>
      {empty ? (
        <span
          className="pointer-events-none absolute inset-y-0 left-3 z-[1] flex items-center text-xs text-muted-foreground/50 sm:text-sm"
          aria-hidden
        >
          <span className="truncate">{placeholder}</span>
        </span>
      ) : null}
      <Input
        ref={inputRef}
        id={id}
        type="time"
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        aria-label={ariaLabel ?? placeholder}
        onChange={(e) => onChange(e.target.value)}
        onClick={openPicker}
        className={cn(
          'toolbar-date-input relative z-0 w-full min-w-0 rounded-xl border pl-3',
          empty ? 'pr-9 text-transparent [&::-webkit-datetime-edit]:text-transparent' : 'pr-14 text-foreground',
          className,
        )}
      />
      <Clock
        className={cn(
          'pointer-events-none absolute top-1/2 z-[1] h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/70',
          empty ? 'right-3' : 'right-8',
        )}
        aria-hidden
      />
      {!empty && !disabled ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onChange('')
          }}
          aria-label={`Clear ${ariaLabel ?? placeholder}`}
          className={cn(
            'absolute right-1.5 top-1/2 z-[2] flex h-6 w-6 -translate-y-1/2 items-center justify-center',
            'rounded-md text-muted-foreground transition-colors',
            'hover:bg-secondary hover:text-foreground',
          )}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  )
}
